// Where an institute stands with Prepgen, and whether it can take on students.
//
// The server enforces all of this; the console only explains it. A new
// institute is `pending` until Prepgen activates it and sets a student cap
// (`maxStudents`). Seats in use are active students plus invites not yet
// answered; a student who asked to join with the code holds no seat until
// approved. The org payload carries `organizationStatus`, `maxStudents` and
// `seatsUsed`; `status` on it is the owner's membership, not the institute's.

export const PENDING_BANNER =
  'Your institute is being reviewed. You can set up exams, batches and tests now; '
  + 'inviting students unlocks once it is activated.';

export const SUSPENDED_BANNER =
  'Your institute is suspended. Your students cannot use it, and you cannot add '
  + 'students, until it is reactivated.';

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * The institute's lifecycle status. Institutes created before activation
 * existed report none, and are active.
 */
export function instituteStatus(org) {
  return org?.organizationStatus || 'active';
}

/** Seats in use against the cap. `max` is null when there is no cap. */
export function seatUsage(org) {
  const max = Number.isInteger(org?.maxStudents) ? org.maxStudents : null;
  const used = Number.isInteger(org?.seatsUsed) ? org.seatsUsed : 0;
  return {
    used,
    max,
    hasCap: max !== null,
    atCap: max !== null && used >= max,
  };
}

/** "12 of 50 students", or null when the institute has no cap. */
export function seatUsageLabel(org) {
  const { used, max, hasCap } = seatUsage(org);
  return hasCap ? `${used} of ${plural(max, 'student')}` : null;
}

/** The console-wide banner for an institute that is not active, or null. */
export function instituteBanner(org) {
  const status = instituteStatus(org);
  if (status === 'pending') return PENDING_BANNER;
  if (status === 'suspended') return SUSPENDED_BANNER;
  return null;
}

/**
 * Why the owner cannot add a student right now (invite or approve a request),
 * or null when they can.
 */
export function addStudentBlockedReason(org) {
  const status = instituteStatus(org);
  if (status === 'pending') return 'Inviting students unlocks once your institute is activated.';
  if (status === 'suspended') return 'Your institute is suspended, so students cannot be added.';

  const { atCap, max } = seatUsage(org);
  if (atCap) {
    return `Your institute has reached its limit of ${plural(max, 'student')}. `
      + 'Remove a student or an unanswered invite to free a seat.';
  }
  return null;
}

/**
 * True while the institute's standing can only change from Prepgen's side:
 * it is waiting to be activated, or it is suspended. The console re-checks
 * during this time so the owner sees the change without reloading.
 */
export function awaitsPrepgen(org) {
  if (!org) return false;
  const status = instituteStatus(org);
  return status === 'pending' || status === 'suspended';
}

/** Whether a fresh copy of the institute differs in status or student cap. */
export function standingChanged(before, after) {
  if (!before || !after) return Boolean(before) !== Boolean(after);
  return instituteStatus(before) !== instituteStatus(after)
    || (before.maxStudents ?? null) !== (after.maxStudents ?? null);
}
